import React, { useState } from 'react';
import { landlordAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Loader2, Send, UserPlus, Link2, Copy, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export const AddTenantDialog = ({ open, onOpenChange, onSuccess }) => {
  const [addTenantForm, setAddTenantForm] = useState({ name: '', phone: '', email: '' });
  const [addingTenant, setAddingTenant] = useState(false);
  const [addTenantMode, setAddTenantMode] = useState('');

  const reset = () => {
    setAddTenantMode('');
    setAddTenantForm({ name: '', phone: '', email: '' });
  };

  const handleAddTenant = async () => {
    if (addTenantMode === 'invite') {
      if (!addTenantForm.name || !addTenantForm.email) { toast.error('Name and email are required'); return; }
      setAddingTenant(true);
      try {
        await landlordAPI.invitePendingTenant({ name: addTenantForm.name, email: addTenantForm.email, phone: addTenantForm.phone || '' });
        toast.success(`Invitation sent to ${addTenantForm.email}!`);
        onOpenChange(false);
        reset();
        onSuccess();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send invitation');
      } finally { setAddingTenant(false); }
    } else if (addTenantMode === 'manual') {
      if (!addTenantForm.name) { toast.error('Name is required'); return; }
      setAddingTenant(true);
      try {
        await landlordAPI.createManualTenant({ name: addTenantForm.name, phone: addTenantForm.phone, email: addTenantForm.email || '' });
        toast.success('Tenant added as manual entry');
        onOpenChange(false);
        reset();
        onSuccess();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add tenant');
      } finally { setAddingTenant(false); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Tenant</DialogTitle>
          <DialogDescription>Choose how you'd like to onboard a tenant</DialogDescription>
        </DialogHeader>

        {!addTenantMode ? (
          <div className="space-y-3">
            {/* Option 1: Invite via Email */}
            <button
              onClick={() => setAddTenantMode('invite')}
              className="w-full flex items-center gap-4 p-4 rounded-lg border hover:border-[#C85A42] hover:bg-[#C85A42]/5 transition-colors text-left group"
              data-testid="add-tenant-invite-option"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium group-hover:text-[#C85A42]">Invite via Email</p>
                <p className="text-xs text-muted-foreground">Send an email invitation for the tenant to register</p>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </button>

            {/* Option 2: Share Registration Link */}
            <button
              onClick={async () => {
                try {
                  const res = await landlordAPI.getRegistrationLink();
                  await navigator.clipboard.writeText(res.data.registration_link);
                  toast.success('Registration link copied to clipboard!');
                } catch (error) {
                  toast.error('Failed to get registration link');
                }
              }}
              className="w-full flex items-center gap-4 p-4 rounded-lg border hover:border-[#C85A42] hover:bg-[#C85A42]/5 transition-colors text-left group"
              data-testid="add-tenant-link-option"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center shrink-0">
                <Link2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium group-hover:text-[#C85A42]">Share Registration Link</p>
                <p className="text-xs text-muted-foreground">Copy a link to share via messaging apps or social media</p>
              </div>
              <Copy className="w-4 h-4 ml-auto text-muted-foreground" />
            </button>

            {/* Option 3: Manual Entry */}
            <button
              onClick={() => setAddTenantMode('manual')}
              className="w-full flex items-center gap-4 p-4 rounded-lg border hover:border-[#C85A42] hover:bg-[#C85A42]/5 transition-colors text-left group"
              data-testid="add-tenant-manual-option"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shrink-0">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium group-hover:text-[#C85A42]">Manual Entry</p>
                <p className="text-xs text-muted-foreground">Add a tenant record now, they can register later</p>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={() => setAddTenantMode('')}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              &larr; Back to options
            </button>

            {addTenantMode === 'invite' && (
              <>
                <p className="text-sm text-muted-foreground">Enter the tenant's details. They'll receive an email with a registration link.</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Name *</Label>
                    <Input value={addTenantForm.name} onChange={(e) => setAddTenantForm({ ...addTenantForm, name: e.target.value })} placeholder="Full name" data-testid="add-tenant-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email *</Label>
                    <Input type="email" value={addTenantForm.email} onChange={(e) => setAddTenantForm({ ...addTenantForm, email: e.target.value })} placeholder="tenant@email.com" data-testid="add-tenant-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone</Label>
                    <Input value={addTenantForm.phone} onChange={(e) => setAddTenantForm({ ...addTenantForm, phone: e.target.value })} placeholder="Optional" data-testid="add-tenant-phone" />
                  </div>
                </div>
              </>
            )}

            {addTenantMode === 'manual' && (
              <>
                <p className="text-sm text-muted-foreground">Add basic info now. You can invite them to register later from their profile.</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Name *</Label>
                    <Input value={addTenantForm.name} onChange={(e) => setAddTenantForm({ ...addTenantForm, name: e.target.value })} placeholder="Full name" data-testid="add-tenant-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone</Label>
                    <Input value={addTenantForm.phone} onChange={(e) => setAddTenantForm({ ...addTenantForm, phone: e.target.value })} placeholder="Optional" data-testid="add-tenant-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={addTenantForm.email} onChange={(e) => setAddTenantForm({ ...addTenantForm, email: e.target.value })} placeholder="Optional" data-testid="add-tenant-email-optional" />
                  </div>
                </div>
              </>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={reset}>Cancel</Button>
              <Button onClick={handleAddTenant} disabled={addingTenant} className="bg-[#C85A42] hover:bg-[#B54A35]" data-testid="save-add-tenant-btn">
                {addingTenant ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : addTenantMode === 'invite' ? <Send className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                {addTenantMode === 'invite' ? 'Send Invitation' : 'Add Tenant'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
